import { difference, merge, mapValues } from 'lodash';
import React from 'react';

const defaultFallbackColor = "#000000";
const defaultFallbackColorBright = "#FFFFFF";
function rgbaToHexA(rgbaArr, withAlpha) {
  const rgba = rgbaArr.map((r2, index) => {
    if (r2.indexOf("%") > -1) {
      const p = parseFloat(r2.substr(0, r2.length - 1)) / 100;
      if (index < 3) {
        return Math.round(p * 255);
      }
      return p;
    }
    return parseFloat(r2);
  });
  let r = Number(rgba[0]).toString(16), g = Number(rgba[1]).toString(16), b = Number(rgba[2]).toString(16), a = Math.round(Number(rgba[3]) * 255).toString(16);
  if (r.length === 1)
    r = "0" + r;
  if (g.length === 1)
    g = "0" + g;
  if (b.length === 1)
    b = "0" + b;
  if (a.length === 1)
    a = "0" + a;
  return "#" + r + g + b + (withAlpha ? a : "");
}
function toHexFormat(color) {
  if (color === "transparent") {
    return defaultFallbackColorBright + "00";
  }
  if ((color.startsWith("rgb(") || color.startsWith("rgba(")) && color.endsWith(")")) {
    const rgba = color.replace(/^(rgba\()|^(rgb\()|(\s)|(\))$/g, "").split(",");
    if (rgba.length === 4) {
      return rgbaToHexA(rgba, true);
    } else if (rgba.length === 3) {
      return rgbaToHexA(rgba);
    } else
      throw Error("[ricos-common] themeUtils.ts: Bad RGB / RGBA value: " + color);
  }
  if (!color.startsWith("#")) {
    throw Error(
      `[ricos-common] themeUtils.ts: Bad Hex (${color}).
      Ricos color can only accept "transparent" or a HEX formatted color as its value`
    );
  }
  return color;
}
function getBrightness(hexCode) {
  const _hexCode = hexCode.replace("#", "");
  const r = parseInt(_hexCode.substr(0, 2), 16);
  const g = parseInt(_hexCode.substr(2, 2), 16);
  const b = parseInt(_hexCode.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1e3;
}
function isBright(hexColor) {
  if (!hexColor) {
    return false;
  }
  return getBrightness(hexColor) > 150;
}
function adaptForeground(actionColor, fallbackColor) {
  if (!actionColor) {
    return void 0;
  }
  return getBrightness(actionColor) < 150 ? actionColor : fallbackColor || defaultFallbackColor;
}
function hexToRgbA(hexColor) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(
    hexColor.toLowerCase()
  );
  if (result) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
      a: parseInt(result[4], 16)
    };
  }
  throw new Error("Bad Hex");
}
function toRgbTuple(hexColor) {
  if (!hexColor) {
    return void 0;
  }
  const { r, g, b } = hexToRgbA(hexColor);
  return `${r}, ${g}, ${b}`;
}
function toCssRgbA(hexColor, opacity) {
  if (!hexColor) {
    return hexColor;
  }
  if (/^#([A-Fa-f\d]{2}){1,4}$/.test(hexColor)) {
    const { r, g, b, a } = hexToRgbA(hexColor);
    return `rgba(${r}, ${g}, ${b}, ${opacity * (a || 1)})`;
  }
  throw new Error("[ricos-common] themeUtils.ts: Bad Hex");
}
function toCssRgb(rgbTuple) {
  return `rgb(${rgbTuple})`;
}
const toDashedKey = (str) => str.replace(/([A-Z])/g, (all, letter) => "-" + letter.toLowerCase());
const spacing = " ".repeat(4);
const toVarStrings = (varsObject) => {
  const convertToRicosKey = (key) => "--ricos-" + toDashedKey(key);
  const cleanFromSemicolons = (value) => typeof value === "string" ? value.split(";")[0] : value;
  return Object.entries(varsObject).filter((entry) => !!entry[1]).map((entry) => convertToRicosKey(entry[0]) + ": " + cleanFromSemicolons(entry[1]) + ";\n").join(spacing);
};
const buildCssVars = (parentClass, ...varObjects) => `
  ${parentClass ? `.${parentClass}` : "*"} {
    ${varObjects.map(toVarStrings).join(spacing).replace(/[\s\t]+$/, "")}
  }
`;

var utils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  adaptForeground: adaptForeground,
  buildCssVars: buildCssVars,
  isBright: isBright,
  toCssRgb: toCssRgb,
  toCssRgbA: toCssRgbA,
  toDashedKey: toDashedKey,
  toHexFormat: toHexFormat,
  toRgbTuple: toRgbTuple,
  toVarStrings: toVarStrings
});

const darkTheme = {
  textColor: "#000000",
  bgColor: "#FFFFFF",
  actionColor: "#15E0FB"
};
const presets = {
  darkTheme
};

const BG_COLOR = 11;
const DISABLED_COLOR = 13;
const TEXT_COLOR_LOW = 14;
const TEXT_COLOR = 15;
const ACTION_COLOR = 18;
const COLORS = { BG_COLOR, DISABLED_COLOR, TEXT_COLOR_LOW, TEXT_COLOR, ACTION_COLOR };
const assertWixPalette = (palette) => {
  if (palette.length === 0) {
    throw Error("Received empty palette colors array");
  }
  const receivedColors = palette.map((val) => val.name);
  const missingColors = difference(
    Object.values(COLORS).map((num) => `color_${num}`),
    receivedColors
  );
  if (missingColors.length > 0) {
    const error = [];
    error.push(
      "Some palette colors were not supplied:",
      `
${missingColors}
`,
      "Palette array must include the following colors:",
      Object.entries(COLORS).map((entry) => `${entry[1]} - ${entry[0]}`).toString().split(",").join("\n"),
      ""
    );
    throw Error(error.join("\n"));
  }
  return palette;
};
const isRicosPalette = (palette) => palette && typeof palette !== "string" && !Array.isArray(palette) && (palette.actionColor || palette.textColor || palette.bgColor);
const getColorByCode = (wixPalette, code) => {
  const idx = code <= 5 ? code - 1 : code - 6;
  return wixPalette[idx];
};
const getColorValue = (wixPalette, code) => getColorByCode(wixPalette, code).value;

const isRicosPaletteRgb = (palette) => typeof palette === "object" && "type" in palette && palette.type === "rgb";

var __defProp$3 = Object.defineProperty;
var __getOwnPropSymbols$3 = Object.getOwnPropertySymbols;
var __hasOwnProp$3 = Object.prototype.hasOwnProperty;
var __propIsEnum$3 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$3 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$3.call(b, prop))
      __defNormalProp$3(a, prop, b[prop]);
  if (__getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(b)) {
      if (__propIsEnum$3.call(b, prop))
        __defNormalProp$3(a, prop, b[prop]);
    }
  return a;
};
const createCssVars = (colors, config) => {
  const { contentBgColor = false, focusActionColor, settingsActionColor } = config || {};
  const isPaletteRgb = isRicosPaletteRgb(colors);
  const {
    textColor,
    bgColor: backgroundColor,
    actionColor,
    fallbackColor = isPaletteRgb ? "0, 0, 0" : "#000000",
    disabledColor,
    textColorLow
  } = colors;
  const parseColors = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      if (value !== void 0) {
        acc[key] = isPaletteRgb ? toCssRgb(value) : value;
        acc[`${key}Tuple`] = isPaletteRgb ? value : toRgbTuple(value);
      }
      return acc;
    }, {});
  };
  const parsedValues = parseColors({
    textColor,
    actionColor,
    backgroundColor,
    fallbackColor,
    disabledColor,
    textColorLow,
    settingsActionColor,
    focusActionColor
  });
  const bgColorContainer = !contentBgColor || backgroundColor === void 0 ? void 0 : isPaletteRgb ? toCssRgb(backgroundColor) : backgroundColor;
  const actionColorFallback = isPaletteRgb ? `rgb(${fallbackColor})` : adaptForeground(actionColor, fallbackColor);
  const actionColorFallbackTuple = isPaletteRgb ? fallbackColor : toRgbTuple(adaptForeground(actionColor, fallbackColor));
  return __spreadValues$3(__spreadValues$3(__spreadValues$3(__spreadValues$3({}, parsedValues), actionColorFallback && { actionColorFallback }), actionColorFallbackTuple && { actionColorFallbackTuple }), bgColorContainer && { bgColorContainer });
};
const extractColors = (palette) => {
  if (typeof palette === "string") {
    if (palette in presets) {
      return presets[palette];
    } else {
      throw Error(`Palette ${palette} is unknown. Supported themes: ${presets.toString()}`);
    }
  } else if (Array.isArray(palette)) {
    assertWixPalette(palette);
    return {
      actionColor: getColorValue(palette, COLORS.ACTION_COLOR),
      bgColor: getColorValue(palette, COLORS.BG_COLOR),
      textColor: getColorValue(palette, COLORS.TEXT_COLOR),
      disabledColor: getColorValue(palette, COLORS.DISABLED_COLOR),
      textColorLow: getColorValue(palette, COLORS.TEXT_COLOR_LOW)
    };
  } else if (palette && (isRicosPalette(palette) || isRicosPaletteRgb(palette))) {
    return palette;
  }
  throw Error("Unrecognized Palette object. Please refer to Ricos Theme Documentation");
};
function createPalette(palette, config) {
  if (!palette) {
    return { paletteVarsObject: {} };
  }
  const isPalleteRgb = isRicosPaletteRgb(palette);
  const colors = extractColors(palette);
  Object.entries(colors).forEach(
    ([colorName, value]) => colors[colorName] = value && typeof value === "string" && !isPalleteRgb ? toHexFormat(value) : value
  );
  const paletteVarsObject = createCssVars(colors, config);
  return { paletteVarsObject, colors };
}

var __defProp$2 = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const toEntries = (wixTypography) => Object.entries(wixTypography);
const definitionToCssFont = (def) => def.value.substr(5, def.value.length - 6);
const toVars$1 = (wixTypography) => toEntries(wixTypography).reduce(
  (prev, curr) => __spreadProps(__spreadValues$2({}, prev), {
    [`font-${curr[0].toLowerCase()}`]: definitionToCssFont(curr[1])
  }),
  {}
);
function createTypography(typography) {
  if (!typography) {
    return {};
  }
  const { fontFamily, wixTypography } = typography;
  const wixTypographyVars = wixTypography ? toVars$1(wixTypography) : {};
  return Object.assign(wixTypographyVars, fontFamily && { fontFamily });
}

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
function fixLineHeight(obj) {
  return __spreadValues$1(__spreadValues$1({}, mapValues(obj, (value) => {
    return typeof value === "object" && value !== null ? fixLineHeight(value) : value;
  })), obj.fontSize !== void 0 && obj.lineHeight === void 0 ? { lineHeight: 1.5 } : {});
}
function toVars(customStyles, prefix = "", separator = "-") {
  const propertyNames = {};
  function traverseObject(currentObj, currentPath = "") {
    for (const [key, value] of Object.entries(currentObj)) {
      const fullPath = currentPath ? `${currentPath}${separator}${key}` : key;
      if (typeof value === "object") {
        traverseObject(value, fullPath);
      } else {
        propertyNames[`${prefix ? prefix + "-" : ""}${fullPath}`] = value;
      }
    }
  }
  traverseObject(customStyles);
  return propertyNames;
}
const createCustomStyles = ({
  customStyles = {},
  settingsStyles = {},
  nativeStyle = {}
}) => {
  return merge(
    {},
    toVars(fixLineHeight(customStyles), "custom"),
    toVars(settingsStyles, "settings"),
    toVars(nativeStyle, "native")
  );
};

const modalTheme = {
  content: {}
};
const defaultTheme = {
  modalTheme
};

var lib = {};

Object.defineProperty(lib, "__esModule", { value: true });
lib.hasValueAtKey = lib.hasPresentKey = lib.isFilled = isDefined_1 = lib.isDefined = lib.isPresent = void 0;
function isPresent(t) {
    return t !== undefined && t !== null;
}
lib.isPresent = isPresent;
function isDefined(t) {
    return t !== undefined;
}
var isDefined_1 = lib.isDefined = isDefined;
function isFilled(t) {
    return t !== null;
}
lib.isFilled = isFilled;
/**
 * Returns a function that can be used to filter down objects
 * to the ones that have a defined non-null value under the key `k`.
 *
 * @example
 * ```ts
 * const filesWithUrl = files.filter(file => file.url);
 * files[0].url // In this case, TS might still treat this as undefined/null
 *
 * const filesWithUrl = files.filter(hasPresentKey("url"));
 * files[0].url // TS will know that this is present
 * ```
 *
 * See https://github.com/microsoft/TypeScript/issues/16069
 * why is that useful.
 */
function hasPresentKey(k) {
    return function (a) {
        return a[k] !== undefined && a[k] !== null;
    };
}
lib.hasPresentKey = hasPresentKey;
/**
 * Returns a function that can be used to filter down objects
 * to the ones that have a specific value V under a key `k`.
 *
 * @example
 * ```ts
 * type File = { type: "image", imageUrl: string } | { type: "pdf", pdfUrl: string };
 * const files: File[] = [];
 *
 * const imageFiles = files.filter(file => file.type === "image");
 * files[0].type // In this case, TS will still treat it  as `"image" | "pdf"`
 *
 * const filesWithUrl = files.filter(hasValueKey("type", "image" as const));
 * files[0].type // TS will now know that this is "image"
 * files[0].imageUrl // TS will know this is present, because already it excluded the other union members.
 *
 * Note: the cast `as const` is necessary, otherwise TS will only know that type is a string.
 * ```
 *
 * See https://github.com/microsoft/TypeScript/issues/16069
 * why is that useful.
 */
function hasValueAtKey(k, v) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (a) {
        return a[k] === v;
    };
}
lib.hasValueAtKey = hasValueAtKey;

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
function themeStrategy(args) {
  var _a, _b, _c, _d, _e, _f;
  const { buildCssVars } = utils;
  const { ricosTheme = {}, plugins = [], cssOverride = {} } = args;
  const {
    parentClass = "",
    palette,
    paletteConfig,
    typography,
    customStyles,
    settingsStyles,
    nativeStyle,
    breakout
  } = ricosTheme;
  const themeGeneratorFunctions = plugins.map((plugin) => plugin.theme).filter(isDefined_1);
  const { paletteVarsObject, colors } = createPalette(palette, paletteConfig);
  const typographyVarsObject = createTypography(typography);
  const customsVarsObject = createCustomStyles({
    customStyles,
    settingsStyles,
    nativeStyle
  });
  const breakoutVars = breakout ? {
    "breakout-normal-padding-start": (_a = breakout == null ? void 0 : breakout.normalPadding.start) != null ? _a : "0",
    "breakout-normal-padding-end": (_b = breakout == null ? void 0 : breakout.normalPadding.end) != null ? _b : "0",
    "breakout-full-width-padding-start": (_d = (_c = breakout == null ? void 0 : breakout.fullWidthPadding) == null ? void 0 : _c.start) != null ? _d : "0",
    "breakout-full-width-padding-end": (_f = (_e = breakout == null ? void 0 : breakout.fullWidthPadding) == null ? void 0 : _e.end) != null ? _f : "0"
  } : {};
  const themeData = {
    colors,
    utils,
    customStyles
  };
  if (colors) {
    themeGeneratorFunctions.forEach((themeGen) => themeGen({ colors, utils, customStyles }));
  }
  const html = /* @__PURE__ */ React.createElement("style", { type: "text/css", key: "styleElement" }, buildCssVars(
    parentClass,
    paletteVarsObject,
    typographyVarsObject,
    customsVarsObject,
    breakoutVars
  ));
  return {
    theme: __spreadValues(__spreadValues({}, defaultTheme), cssOverride),
    html,
    themeData
  };
}

export { createPalette, themeStrategy, utils as themeUtils };
